import { ProStoragePlan } from "ts/api/constraints";
import { useCurrentShowId } from "hooks";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";
import { isProStoragePlan } from "tools/show/paidFeatures/proStorage";

type UseCurrentProStoragePlanReturn = [ProStoragePlan?, boolean?];

export const proStoragePlanQueryKey = (showId?: string) => [
  RequestKey.GetPaidFeatureBoughtForProStorageApiV2,
  showId,
];

// A 404 from this endpoint means "PRO storage is not bought for this show" —
// an expected, frequent state, not an error. Translate it to `null` inside the
// queryFn so react-query caches the answer instead of retrying it as a failure.
const isNotFound = (e: unknown): boolean => {
  const status =
    (e as { response?: { status?: number }; status?: number } | null)?.response
      ?.status ?? (e as { status?: number } | null)?.status;
  return status === 404;
};

/**
 * The show's currently bought PRO-storage plan.
 *
 * Deliberately backed by react-query and NOT by a zustand action: this hook is
 * mounted once per *Files* cell of a grid response (via
 * `useFileUploadSizeLimit` → `FilesResponseInput`) plus once by
 * `InputFileMaxSize`. The previous version fired the fetch from a per-mount
 * `useEffect` into a shared store with no in-flight dedup or cache, so a task
 * with N file cells issued N identical `GET /paid-features/bought/for-pro-storage`
 * requests on every open. One query key per show collapses them into one call.
 */
export const useCurrentProStoragePlan = (props?: {
  id?: string;
}): UseCurrentProStoragePlanReturn => {
  const currentShowId = useCurrentShowId();
  const showId = props?.id ?? currentShowId;
  const queryClient = useQueryClient();

  const { data: boughtProStorage, isLoading } = useQuery({
    queryKey: proStoragePlanQueryKey(showId),
    queryFn: async () => {
      try {
        return await apiV2.paidFeature.getOnePaidFeatureBoughtForProStorage({
          showId: showId as string,
        });
      } catch (e) {
        if (isNotFound(e)) {
          return null;
        }
        throw e;
      }
    },
    enabled: !!showId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!showId) return;

    const listenerId = `USE_CURRENT_PRO_STORAGE_PLAN:${showId}`;
    const key = NotificationType.PAID_FEATURES_CREATED;

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key,
      showId,
      listenerId,
      callback: async (payload) => {
        if (payload.showId !== showId) return;
        await queryClient.invalidateQueries({
          queryKey: proStoragePlanQueryKey(showId),
        });
      },
    });

    return () => {
      apiV2.notification.unsubscribe({ key, showId, listenerId });
    };
  }, [queryClient, showId]);

  const paidFeature = boughtProStorage?.paidFeature;
  const currentPlan =
    paidFeature && isProStoragePlan(paidFeature) ? paidFeature : undefined;

  return [currentPlan, isLoading];
};
